<script setup lang="ts">
/**
 * In-game legal viewer — the same admin-managed documents the landing footer
 * serves (core CMS `legal_pages`), with the legal entity rendered from the
 * admin-managed `company.*` settings. Nothing here is hardcoded copy.
 */
import { ref, onMounted } from 'vue';
import {
  fetchLegalPage, fetchCompanyInfo, fillCompanyVars, legalDocTitle,
  type CompanyInfo, type LegalPageMeta,
} from '../game/legal-api';
import { t } from '../i18n';

// The tab set is the admin-published directory (GET /api/legal/pages), passed
// in by the opener — hardcoding slugs here would drift from the CMS the moment
// an admin adds or retires a document.
const props = defineProps<{ docs: LegalPageMeta[]; initialSlug?: string }>();
const emit = defineEmits<{ (e: 'close'): void }>();

const active = ref<string>(props.initialSlug ?? props.docs[0]?.slug ?? '');
const title = ref('');
const html = ref('');
const loading = ref(true);
const error = ref(false);
const company = ref<CompanyInfo | null>(null);

let companyOnce: Promise<CompanyInfo | null> | null = null;
const getCompany = () => (companyOnce ??= fetchCompanyInfo().catch(() => null));

async function load(slug: string) {
  active.value = slug;
  loading.value = true;
  error.value = false;
  try {
    const [page, info] = await Promise.all([fetchLegalPage(slug), getCompany()]);
    company.value = info;
    // Same translation rule as the tabs: footer keys for the founding papers,
    // the CMS title as written for anything else.
    title.value = legalDocTitle(page);
    html.value = fillCompanyVars(page.content, info);
  } catch {
    error.value = true;
  } finally {
    loading.value = false;
  }
}

/** Cross-links between policies inside the document body. */
function onBodyClick(e: MouseEvent) {
  const link = (e.target as HTMLElement).closest<HTMLElement>('[data-legal]');
  if (!link) return;
  e.preventDefault();
  e.stopPropagation(); // keep the SPA-wide link interceptor from re-routing under the modal
  void load(link.dataset.legal!);
}

onMounted(() => { if (active.value) void load(active.value); });
</script>

<template>
  <div
    class="fixed inset-0 z-[200] grid place-items-center p-4"
    style="background: rgba(6,4,12,0.82)"
    @click.self="emit('close')"
    @keydown.esc="emit('close')"
  >
    <div
      class="lp-tablet bound-frame w-full max-w-[720px] max-h-[86vh] overflow-y-auto p-6"
      style="box-shadow: var(--shadow-lift)"
    >
      <button
        class="btn-forge btn-forge--carved btn-forge-icon absolute top-3 right-3 z-10"
        :aria-label="t('common.close')"
        @click="emit('close')"
      >&times;</button>

      <h2 class="font-heading text-[22px] gilt-text pr-10">{{ t('legalPanel.title') }}</h2>
      <div class="lp-divider" aria-hidden="true"></div>

      <!-- `doc`, never `t` — a loop variable named `t` shadows the i18n `t`
           (the exact footgun documented in Settings.vue's openTab). -->
      <div class="flex flex-wrap gap-2 mb-4">
        <button
          v-for="doc in props.docs" :key="doc.slug"
          class="notch-tab"
          :class="{ 'notch-tab-active': active === doc.slug }"
          @click="load(doc.slug)"
        >{{ legalDocTitle(doc) }}</button>
      </div>

      <p v-if="loading" class="font-body italic text-text-dim text-sm py-6 text-center">
        {{ t('legalOverlay.loading') }}
      </p>
      <p v-else-if="error" class="font-body text-error text-sm py-6 text-center">
        {{ t('legalOverlay.errorBody') }}
      </p>
      <template v-else>
        <h3 class="lp-title font-heading mb-2">{{ title }}</h3>
        <!-- content is authored by admins in the CMS -->
        <div class="lp-doc font-body text-[13px]" style="color: #bccbdb" v-html="html" @click="onBodyClick"></div>
      </template>

      <address
        v-if="company?.name"
        class="lp-entity mt-5 pt-4 border-t border-border font-label not-italic text-[11px] leading-relaxed text-text-dim/70"
      >
        <b class="text-text-dim">{{ company.name }}</b><br />
        <template v-if="company.registrationNumber">{{ t('legalPanel.companyNumber') }} {{ company.registrationNumber }}<br /></template>
        <template v-if="company.address">{{ t('legalPanel.registeredAddress') }} {{ company.address }}<br /></template>
        <template v-if="company.email">{{ t('legalPanel.contact') }} <a class="text-gold" :href="`mailto:${company.email}`">{{ company.email }}</a></template>
      </address>
    </div>
  </div>
</template>

<style scoped>
/* GRANITE TABLET — same face/title/divider recipe as FaqPanel, so the two
   admin-content viewers read as one family: the house's carved record. */
.lp-tablet {
  position: relative;
  background:
    radial-gradient(80% 34% at 50% 0%, rgba(111, 216, 192, .10), transparent 70%),
    var(--tex-stone),
    linear-gradient(180deg, #131a26, #0c1119 58%, #0e141d);
  background-size: auto, 150px 150px, auto;
  background-blend-mode: normal, soft-light, normal;
  scrollbar-width: thin;
  scrollbar-color: color-mix(in srgb, var(--color-ice-dim) 35%, transparent) transparent;
}
.lp-divider {
  width: 190px; height: 9px;
  margin: 10px 0 14px;
  background: var(--orn-divider-gold) no-repeat center / contain;
}
.lp-title { font-size: 20px; color: var(--color-text); }

.lp-doc :deep(h2) {
  font-family: var(--font-heading);
  color: #d9c08a;
  font-size: 15px;
  margin: 18px 0 6px;
}
.lp-doc :deep(p) { margin: 0 0 10px; }
.lp-doc :deep(ul) { margin: 0 0 10px; padding-left: 18px; list-style: disc; }
.lp-doc :deep(li) { margin-bottom: 4px; }
.lp-doc :deep(a) { color: var(--color-gold); text-decoration: underline; text-underline-offset: 2px; }
.lp-doc :deep(table) { width: 100%; border-collapse: collapse; margin: 6px 0 14px; font-size: 12.5px; }
.lp-doc :deep(th) {
  font-family: var(--font-label);
  font-size: 10px; text-transform: uppercase; letter-spacing: 0.07em;
  text-align: left; color: var(--color-text-dim);
  padding: 6px 8px; border-bottom: 1px solid var(--color-gold, #c9a84c);
}
.lp-doc :deep(td) { padding: 7px 8px; border-bottom: 1px solid var(--color-border, #2a2436); vertical-align: top; }
</style>
